interface RequestInfo {
    promise: Promise<any>;
    timestamp: Date;
    inProgress: boolean;
    expiryMinutes: number;
}

interface RequestManagerOptions {
    expiryMinutes?: number;
    retryDelay?: number;
    maxRetries?: number;
}

// Module-scoped state
const requests: Map<string, RequestInfo> = new Map();
const cooldownTimers: Map<string, ReturnType<typeof setTimeout>> = new Map();
const COOLDOWN_MS = 10_000; // Keep resolved promises cached for 10 seconds
const defaultOptions: RequestManagerOptions = {
    expiryMinutes: 2,
    retryDelay: 2000, // 2 seconds
    maxRetries: 3,
};

/**
 * Configure the request throttle manager with custom options
 * @param options The configuration options
 */
export const configure = (options: RequestManagerOptions): void => {
    if (options.expiryMinutes !== undefined) defaultOptions.expiryMinutes = options.expiryMinutes;
    if (options.retryDelay !== undefined) defaultOptions.retryDelay = options.retryDelay;
    if (options.maxRetries !== undefined) defaultOptions.maxRetries = options.maxRetries;
};

/**
 * Execute a request with throttling and caching
 * @param key Unique key to identify the request (typically endpoint + query params)
 * @param request The request function that returns a promise
 * @returns Promise with the request result
 */
export const execute = async <T>(key: string, request: () => Promise<T>): Promise<T> => {
    const existingRequest = requests.get(key);

    // If there's an existing request in progress or in cooldown, return the cached promise
    if (existingRequest) {
        if (existingRequest.inProgress) {
            console.log(`RequestThrottleManager: Request in progress, waiting for ${key}`);
        } else {
            console.log(`RequestThrottleManager: Returning cached result (cooldown) for ${key}`);
        }
        return existingRequest.promise as Promise<T>;
    }

    // Clear any existing cooldown timer for this key
    const existingTimer = cooldownTimers.get(key);
    if (existingTimer) {
        clearTimeout(existingTimer);
        cooldownTimers.delete(key);
    }

    // Create a new request promise
    const promise = (async () => {
        try {
            const result = await request();
            return result;
        } finally {
            // Mark as no longer in progress but keep the cached result
            const entry = requests.get(key);
            if (entry) {
                entry.inProgress = false;
            }
            // Remove from cache after cooldown period
            const timer = setTimeout(() => {
                requests.delete(key);
                cooldownTimers.delete(key);
            }, COOLDOWN_MS);
            cooldownTimers.set(key, timer);
        }
    })();

    // Store the request as in progress
    requests.set(key, {
        promise,
        timestamp: new Date(),
        inProgress: true,
        expiryMinutes: 0,
    });

    return promise;
};

/**
 * Clear a cached request by key
 * @param key The request key to clear
 */
export const clear = (key: string): void => {
    const timer = cooldownTimers.get(key);
    if (timer) {
        clearTimeout(timer);
        cooldownTimers.delete(key);
    }
    requests.delete(key);
};

/**
 * Clear all cached requests
 */
export const clearAll = (): void => {
    cooldownTimers.forEach((timer) => clearTimeout(timer));
    cooldownTimers.clear();
    requests.clear();
};

/**
 * Check if a request is currently in progress
 * @param key The request key to check
 * @returns boolean indicating if the request is in progress
 */
export const isRequestInProgress = (key: string): boolean => {
    const request = requests.get(key);
    return request ? request.inProgress : false;
};

// Export as a named object for backward compatibility with the previous class-based implementation
export const requestThrottleManager = {
    execute,
    clear,
    clearAll,
    isRequestInProgress,
    configure,
};

export default requestThrottleManager;
